// src/pages/TermsPage.tsx
import React from "react";
import { useTranslation, Trans } from "react-i18next";
import SiteFooter from "../components/SiteFooter";

const LAST_UPDATED = "2025-12-18";

// TODO: Fill these with your real legal info (or load from env/config)
const PROVIDER_NAME = "Andrei Cosmin Marica";
const PROVIDER_ADDRESS = "Via Petrazzo 18, Messina (ME), 98142, Italia";
const SUPPORT_EMAIL = "assistenza@coslo.it";
const PROVIDER_IVA = "IT03858840832";

type SectionKey =
  | "provider"
  | "definitions"
  | "scope"
  | "account"
  | "plansPayments"
  | "permittedUseCompliance"
  | "privacyMultiTenant"
  | "aiLimitations"
  | "thirdParties"
  | "availabilitySupport"
  | "ip"
  | "termination"
  | "warranty"
  | "liability"
  | "indemnity"
  | "changes"
  | "lawForum"
  | "contacts"

const TermsPage: React.FC = () => {
  const { t } = useTranslation();

  const sections: SectionKey[] = [
    "provider",
    "definitions",
    "scope",
    "account",
    "plansPayments",
    "permittedUseCompliance",
    "privacyMultiTenant",
    "aiLimitations",
    "thirdParties",
    "availabilitySupport",
    "ip",
    "termination",
    "warranty",
    "liability",
    "indemnity",
    "changes",
    "lawForum",
    "contacts",
  ];

  const renderList = (key: string) => {
    const items = t(key, { returnObjects: true }) as unknown;
    if (!Array.isArray(items)) return null;

    return (
      <ul className="policy-list">
        {items.map((txt, idx) => (
          <li key={idx}>{String(txt)}</li>
        ))}
      </ul>
    );
  };

  const renderParagraphs = (sectionKey: SectionKey) => {
    const base = `terms.sections.${sectionKey}`;
    const paragraphs = t(`${base}.paragraphs`, { returnObjects: true }) as unknown;

    if (!Array.isArray(paragraphs)) return null;

    const useTrans = t(`${base}.useTrans`, { defaultValue: "false" }) === "true";

    return paragraphs.map((_, idx) => {
      const pKey = `${base}.paragraphs.${idx}`;

      if (!useTrans) {
        return (
          <p key={idx} className="lp-text">
            {t(pKey)}
          </p>
        );
      }

      return (
        <p key={idx} className="lp-text">
          <Trans
            i18nKey={pKey}
            values={{
              lastUpdated: LAST_UPDATED,
              providerName: PROVIDER_NAME,
              providerAddress: PROVIDER_ADDRESS,
              email: SUPPORT_EMAIL,
            }}
            components={{
              a: <a href={`mailto:${SUPPORT_EMAIL}`} />,
              strong: <strong />,
            }}
          />
        </p>
      );
    });
  };

  return (
    <div className="policy-page">
      <div className="lp-container policy-container">
        <header className="policy-header">
          <p className="policy-kicker">{t("terms.hero.kicker")}</p>
          <h1 className="lp-section-title">{t("terms.hero.title")}</h1>
          <p className="lp-text policy-intro">{t("terms.hero.intro")}</p>
          <p className="lp-text">
            <strong>{t("terms.hero.lastUpdatedLabel")}:</strong> {LAST_UPDATED}
          </p>
        </header>

        <div className="policy-grid">
          {sections.map((k) => (
            <section key={k} className="policy-section">
              <h2 className="policy-subtitle">{t(`terms.sections.${k}.title`)}</h2>

              {/* Special provider block (inserts your legal constants) */}
              {k === "provider" ? (
                <p className="lp-text">
                  <strong>{t("terms.sections.provider.labels.provider")}:</strong>{" "}
                 {PROVIDER_NAME + ("(P.IVA: " + PROVIDER_IVA + ")")}
                  <br />
                  <strong>{t("terms.sections.provider.labels.address")}:</strong>{" "}
                  {PROVIDER_ADDRESS}
                  <br />
                  <strong>{t("terms.sections.provider.labels.contact")}:</strong>{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                </p>
              ) : (
                renderParagraphs(k)
              )}

              {/* Optional list per section */}
              {renderList(`terms.sections.${k}.list`)}
            </section>
          ))}
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default TermsPage;
